import { useContext, useEffect, useState } from 'react';
import { Page, Spacer } from '../../components';
import { MutationStatus, useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { UserCompartments, userStorageKey } from '../../data/user';
import { UserData } from '../../models/UserData';
import { ToastbarContext } from '../../App';
import { useLoading } from '../../hooks/useLoading';
import { ArtDashboardTeam } from '../../assets/png';
import { AdminTierTypeEnum, DrillDownWizard, drillDownWizardKey } from '../../services/drillDown';
import { TeamData } from '../../models/TeamData';
import { TeamCompartments, teamStorageKey } from '../../data/team';
import { DeleteTeam } from '../../data/team/mutations';
import { Toast } from '../../models/Toast';
import { GroupCompartments, groupStorageKey } from '../../data/group';
import { GroupData } from '../../models/GroupData';
import AdminTierHeader from '../multitier/AdminTierHeader';
import { AdminPageProps } from '../multitier/AdminDashboard';
import { TeamMetricsData } from '../../models/MetricsData';
import {
  useGroupAvailablePricebooks,
  useAllPricebookBundles,
} from '../../hooks/usePricebookOptions';
import { getSubmittedPricebookIds } from '../pricebook-restrictions/helpers/pricebookRestrictionUtils';
import { TeamListView } from './TeamListView';

const TeamPage: React.FC<AdminPageProps> = ({
  select,
  selectGroup,
  saveData: saveTeamData,
  selectOrganization,
  setOrgStatus,
  setIsNavigatingToOrg,
}) => {
  const appStorage = useAppStorage();
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();
  const [tryAgain, setTryAgain] = useState<boolean>(false);

  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);
  const deleteTeam = useMutation(new DeleteTeam());

  const userDataCache = appStorage.retrieve<UserCompartments>(userStorageKey);
  const teamDataCache = appStorage.retrieve<TeamCompartments>(teamStorageKey);
  const groupDataCache = appStorage.retrieve<GroupCompartments>(groupStorageKey);

  const userData = useObservable(userDataCache.observe$<UserData>('user'));
  const allTeams = useObservable(teamDataCache.observe$<TeamData[]>('allTeams'));
  const highestAccess = useObservable(drillDownWizard.highestAccess$);
  const origin = useObservable(drillDownWizard.origin$);
  const groupData = useObservable(groupDataCache.observe$<GroupData>('group'));
  const numGroups = useObservable(groupDataCache.observe$<GroupData[]>('allGroups'))?.length ?? 0;
  const allTeamMetrics = useObservable(teamDataCache.observe$<TeamMetricsData[]>('allMetrics'));
  const groupAvailablePricebooks = useGroupAvailablePricebooks();
  const allPricebookBundles = useAllPricebookBundles();

  const groupName = groupData?.name ?? drillDownWizard.findGroupNameById();
  const numTeams = allTeams?.length ?? 0;
  const landingPage =
    (highestAccess === AdminTierTypeEnum.Team && numTeams >= 2) ||
    (highestAccess === AdminTierTypeEnum.Group && numGroups < 2) ||
    origin === AdminTierTypeEnum.Team;

  const onCreate = async (values: any) => {
    setLoading(true);
    const pricebookIds = getSubmittedPricebookIds(
      values.restrictionMode,
      values.selectedPricebookIds,
    );
    await saveTeamData?.action({ name: values.name, pricebookIds });
  };

  useEffect(() => {
    if (!tryAgain) {
      return;
    }
    if (deleteTeam.error) {
      setLoading(false);
      setToast(
        new Toast({
          message: 'Error removing team',
          severity: 'error',
          open: true,
        }),
      );
      setTryAgain(false);
    } else if (deleteTeam.status === MutationStatus.Complete) {
      setLoading(false);
      setToast(new Toast({ message: 'Team removed!', severity: 'success', open: true }));
      setTryAgain(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteTeam, tryAgain]);

  if (allTeams === undefined || allTeamMetrics === undefined) {
    return null;
  }

  return (
    <Page
      title={
        landingPage ? (userData?.firstName ? `Hi, ${userData.firstName}!` : 'Hi!') : groupName
      }>
      <Spacer height='sm' />
      <AdminTierHeader
        onCreate={onCreate}
        hideCreate={highestAccess !== AdminTierTypeEnum.Group}
        tierIcon={<img src={ArtDashboardTeam} alt='Art Dashboard Team' width={70} height={70} />}
        tier='team'
        groupAvailablePricebooks={
          highestAccess === AdminTierTypeEnum.Group ? groupAvailablePricebooks : undefined
        }
        allPricebookBundles={
          highestAccess === AdminTierTypeEnum.Group ? allPricebookBundles : undefined
        }
      />
      <TeamListView
        teamList={allTeams}
        allMetrics={allTeamMetrics}
        select={select}
        selectGroup={selectGroup}
        currentGroupId={groupData?.id}
        selectOrganization={selectOrganization}
        setOrgStatus={setOrgStatus}
        setIsNavigatingToOrg={setIsNavigatingToOrg}
      />
    </Page>
  );
};

export default TeamPage;
